import fetchClient, { ApiResponse } from '../api-client'
import { ApprovePkgReqPayload } from './types/ApprovePkgReqPayload'
import { CreatePackageReqPayload } from './types/CreatePackageReqPayload'
import { GetPkgReqsPayload } from './types/GetPkgReqsPayload'
import { GetPackageReqsResponse } from './types/GetPkgReqsResponse'
import { GetUnitedPkgDataResponse } from './types/GetUnitedPkgDataResponse'
import { PackageReqStatsResponse } from './types/PackageReqStats'
import { PkgReq } from './types/PkgReq'
import { PackageReqFeesResponse } from './types/PkgReqFeesResponse'
import { RejectPackageReqPayload } from './types/RejectPackageReqPayload'
import { UpdatePackageReqPayload } from './types/UpdatePkgReqPayload'

/**
 * Get package requests with optional filtering
 */
export const getPackageReqs = async (params: GetPkgReqsPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<{ response: GetPackageReqsResponse }>>(`/preqs`, params)
    return res.data.data?.response || {}
  } catch (error) {
    throw error
  }
}
export const getPackageReq = async (id: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<PkgReq>>(`/preqs/${id}`)
    return res.data.data
  } catch (error) {
    throw error
  }
}
/**
 * Get package request fees by package ID
 */
export const getPackageFeesByPackageId = async (packageId: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<{ data: PackageReqFeesResponse }>>(`/preqs/package-fees/${packageId}`)
    return (
      res.data.data?.data || {
        uniteFee: 0,
        photoRequestFeeUSD: 0,
        shrinkRequestFeeUSD: 0,
      }
    )
  } catch (error) {
    throw error
  }
}

/**
 * Create a new package request
 */
export const createPackageReq = async (data: CreatePackageReqPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<CreatePackageReqPayload>>(`/preqs-create`, { data })
    if (!res.data.data) {
      throw new Error('Failed to create package request')
    }
    return res.data.data
  } catch (err) {
    console.error('createPackageReq error:', err)
    throw err
  }
}

/**
 * Reject a package request
 */
export const rejectPackageReq = async (data: RejectPackageReqPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<undefined>>(`/preqs-reject`, { data })
    return res.data
  } catch (err) {
    console.error('rejectPackageReq error:', err)
    throw err
  }
}

/**
 * Update a package request
 */
export const updatePackageReq = async (data: UpdatePackageReqPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<PkgReq>>(`/preqs-update`, { data })
    return res.data.data
  } catch (err) {
    console.error('updatePackageReq error:', err)
    throw err
  }
}

/**
 * Approve a package request
 */
export const approvePackageReq = async (packageRequest: ApprovePkgReqPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<undefined>>(`/preqs-approve`, packageRequest)
    return res.data
  } catch (err) {
    console.error('approvePackageReq error:', err)
    throw err
  }
}

/**
 * Get united package data for a package request
 */
export const getUnitedPackageData = async (packageReqId: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<{ data: GetUnitedPkgDataResponse } | GetUnitedPkgDataResponse>>(
      `/preqs-unite/${packageReqId}`
    )
    if ('data' in res.data.data) {
      return res.data.data.data
    }
    return res.data.data
  } catch (error) {
    throw error
  }
}

/**
 * Download CSV report for all packages in pending unite requests
 */
export const downloadUnitePackagesReport = async (warehouse: string) => {
  try {
    const response = await fetchClient.get(`/preqs-unite-packages-report?warehouse=${warehouse}`, {
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * Get package request statistics
 */
export const getPackageReqStats = async (filter?: { warehouse?: string }): Promise<PackageReqStatsResponse> => {
  try {
    const res = await fetchClient.post<ApiResponse<PackageReqStatsResponse>>(`/preqs/stats`, filter)
    return res.data.data
  } catch (error) {
    throw error
  }
}
